'use client'

import { Suspense, useEffect, useEffectEvent, useState } from 'react'
import { useAtomValue } from 'jotai'
import { useProductData } from '@/components/ProductData/ProductDataProvider'
import { useOfferToEdit } from '@/components/ProductPage/useOfferToEdit'
import { useRecommendations } from '@/features/CrossSell/hooks/useRecommendations'
import { priceCalculatorAddedOffer } from '@/features/priceCalculator/PurchaseForm/priceCalculatorAtoms'
import { PurchaseForm } from '@/features/priceCalculator/PurchaseForm/PurchaseForm'
import { PurchaseSummary } from '@/features/priceCalculator/PurchaseForm/PurchaseSummary/PurchaseSummary'
import { usePrefillFromRecommendation } from '@/features/priceCalculator/usePrefillFromRecommendation'
import { PriceIntentAttributionCapture } from '@/features/recommendations/PriceIntentAttributionCapture'
import { usePriceIntentAttribution } from '@/features/recommendations/usePriceIntentAttribution'
import { priceIntentServiceInitClientSide } from '@/services/priceIntent/PriceIntentService'
import { useTracking } from '@/services/Tracking/useTracking'
import { useResponsiveVariant } from '@/utils/useResponsiveVariant'
import { useApolloClient } from '@apollo/client'
import { ProductHeroPillow } from '../../ProductHero/ProductHeroPillow/ProductHeroPillow'
import { StickyProductHeader } from '../../ProductHero/StickyProductHeader/StickyProductHeader'
import { ProductSpecs } from '../../ProductSpecs/ProductSpecs'
import { PurchaseSummaryWithRecommendations } from '../../PurchaseForm/PurchaseSummary/PurchaseSummaryWithRecommendations'
import { usePriceCalculatorStep } from '../../usePriceCalculatorStep'
import { usePriceIntent } from '../../usePriceIntent'
import { CartToast } from './CartToast/CartToast'
import {
  pageGrid,
  priceCalculatorSection,
  productHeroSection,
  purchaseSummary,
  purchaseSummarySection,
} from './PriceCalculatorCmsPageContent.css'

export function PriceCalculatorCmsPageContent() {
  useReportOpenPriceCalculator()

  const variant = useResponsiveVariant('lg')
  const addedOfferToCart = useAtomValue(priceCalculatorAddedOffer)
  const offerToEdit = useOfferToEdit()
  const { getPriceIntentAttributionField, clearAfterSuccessfulConfirm } = usePriceIntentAttribution()

  const { offerRecommendations, productRecommendations } = useRecommendations()

  const hasExternalRecommendations =
    offerRecommendations.some((rec) => rec.externalInsurance != null) || productRecommendations.length > 0

  // Keep showing PurchaseSummaryWithRecommendations once we've decided to show it for the current
  // added offer. Otherwise, adding the only recommended offer to the cart would empty the
  // recommendations list and unmount the component, switching the user to the plain PurchaseSummary
  // and losing the "added recommendation" state.
  const [shouldShowRecommendations, setShouldShowRecommendations] = useState(false)

  const syncShouldShowRecommendations = useEffectEvent(() => {
    if (!addedOfferToCart) {
      setShouldShowRecommendations(false)
    } else if (hasExternalRecommendations) {
      setShouldShowRecommendations(true)
    }
  })

  useEffect(() => {
    syncShouldShowRecommendations()
  }, [addedOfferToCart, hasExternalRecommendations])

  const priceIntentId = offerToEdit?.isInCart ? offerToEdit.offer.priceIntentId : null
  const needsPopulation = offerToEdit && priceIntentId == null

  const { priceIntent } = usePriceIntent(priceIntentId)
  const { step, setStep } = usePriceCalculatorStep()

  const [isPopulated, setIsPopulated] = useState(false)
  const apolloClient = useApolloClient()

  useEffect(() => {
    if (!needsPopulation || !priceIntent || isPopulated) return
    if (priceIntent.offers.length > 0) {
      setIsPopulated(true)
      setStep('viewOffers')
      return
    }
    const service = priceIntentServiceInitClientSide(apolloClient)

    // We are using the offer’s priceIntentData to populate a new price intent
    // We need to apply suggested data to the new price intent if the source offer was priced with suggested data
    // This flow is triggered when editing an offer without a priceIntentId like accident and cross-sell offers generated on storefront
    const sourceOfferHasSuggestedData = offerToEdit.offer.priceIntentData.containsSuggestedData === true

    service
      .updateAndConfirm({
        priceIntentId: priceIntent.id,
        data: offerToEdit.offer.priceIntentData,
        applySuggestedData: sourceOfferHasSuggestedData,
        // Skip generating recommendations while editing an offer
        generateRecommendations: false,
        ...getPriceIntentAttributionField(priceIntent.product.name),
      })
      .then(() => {
        clearAfterSuccessfulConfirm(priceIntent.product.name)
        setIsPopulated(true)
        setStep('viewOffers')
      })
  }, [
    needsPopulation,
    priceIntent,
    isPopulated,
    apolloClient,
    offerToEdit,
    getPriceIntentAttributionField,
    clearAfterSuccessfulConfirm,
    setStep,
  ])

  const { isPrefilling } = usePrefillFromRecommendation(priceIntent)

  const isReady = (!needsPopulation || isPopulated) && !isPrefilling

  const [isCondensed, setIsCondensed] = useState(false)

  const showProductHero = !addedOfferToCart
  // Hide some of the header menu items and show a back button
  // to product page, except when an offer is added to cart
  const hideHeaderMenu = !addedOfferToCart

  return (
    <main className={pageGrid} data-hide-header-menu={hideHeaderMenu}>
      {/* PriceIntentAttributionCapture reads URL search params, which need Suspense during App Router prerender. */}
      <Suspense fallback={null}>
        <PriceIntentAttributionCapture />
      </Suspense>

      {showProductHero && (
        <section className={productHeroSection}>
          {priceIntent && <StickyProductHeader setIsCondensed={setIsCondensed} />}
          <ProductHeroPillow isCondensed={isCondensed} />
          {variant === 'desktop' && step === 'viewOffers' && <ProductSpecs />}
        </section>
      )}

      {!addedOfferToCart && isReady && (
        <section className={priceCalculatorSection}>
          <PurchaseForm />
        </section>
      )}

      {addedOfferToCart && (
        <section className={purchaseSummarySection}>
          {shouldShowRecommendations ? (
            <PurchaseSummaryWithRecommendations className={purchaseSummary} />
          ) : (
            <PurchaseSummary className={purchaseSummary} />
          )}
        </section>
      )}

      {/* We don't mount CartToast on mobile as we hide ShoppingCartMenuItem at that level */}
      {variant === 'desktop' && <CartToast />}
    </main>
  )
}

function useReportOpenPriceCalculator() {
  const tracking = useTracking()
  const productData = useProductData()

  useEffect(() => {
    tracking.reportOpenPriceCalculator({
      id: productData.id,
      displayNameFull: productData.displayNameFull,
    })
  }, [tracking, productData])
}
